// components/create/EventTypeSelector.js
//
// Picking what kind of event this is.
//
// ── Why a sheet rather than the dropdown that was here ─────────────────────
//
// The dropdown opened BEHIND the cards below it. Not a z-index that was too
// low — it was 10, which normally wins — but because every `.section-card` on
// this form carries `backdrop-filter`, and an element with a backdrop filter
// is promoted to its own composited layer and painted as a unit. A later
// sibling promoted that way lands on top of an earlier sibling's absolutely
// positioned child whatever its z-index says, because they are no longer in
// the same painting pass. Raising the number does not fix it; taking the menu
// out of the flow does.
//
// So this is the app's arrangement instead: a sheet, with a search box, over
// everything. Which is also the only thing that works for a list of seventy —
// a 300px scrolling dropdown with no search was tolerable for eleven items and
// is not for seventy.
//
// ── Custom types ───────────────────────────────────────────────────────────
//
// "Other" opens a text field, as it does in the app. `event_type` is a free
// text column, so a custom label is stored as-is; it just will not match any
// cover templates, which are keyed on the known labels.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FiX, FiSearch } from 'react-icons/fi';
import { EVENT_TYPES } from '../../constants/eventTypes';
import '../../styles/EventTypeSelector.scss';
import Portal from '../Portal';

export default function EventTypeSelector({ value, onSelect, onClose }) {
  const [query, setQuery] = useState('');
  const [custom, setCustom] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Straight into the search box: with seventy options, typing is how most
    // people will find theirs.
    searchRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return EVENT_TYPES;
    return EVENT_TYPES.filter((t) => t.label.toLowerCase().includes(q));
  }, [query]);

  const choose = (type) => {
    if (type.label === 'Other') {
      setShowCustom(true);
      return;
    }
    onSelect(type.label);
    onClose();
  };

  const saveCustom = () => {
    const label = custom.trim();
    if (!label) return;
    onSelect(label);
    onClose();
  };

  return (
    <Portal>
    <div className="etype-scrim" onClick={onClose}>
      <div
        className="etype-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Event type"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="etype-head">
          <h2>Event type</h2>
          <button type="button" onClick={onClose} aria-label="Close"><FiX /></button>
        </div>

        {showCustom ? (
          <div className="etype-custom">
            <label htmlFor="etype-custom-input">What kind of event is it?</label>
            <input
              id="etype-custom-input"
              type="text"
              value={custom}
              maxLength={40}
              placeholder="e.g. Satyanarayan Katha"
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveCustom(); }}
              autoFocus
            />
            <div className="etype-custom-actions">
              <button type="button" className="etype-btn" onClick={() => setShowCustom(false)}>
                Back
              </button>
              <button
                type="button"
                className="etype-btn etype-btn--primary"
                onClick={saveCustom}
                disabled={!custom.trim()}
              >
                Use this
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="etype-search">
              <FiSearch />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search event types…"
                aria-label="Search event types"
              />
            </div>

            <div className="etype-list" role="listbox" aria-label="Event types">
              {filtered.length === 0 ? (
                <p className="etype-empty">
                  Nothing matches “{query}”.{' '}
                  <button type="button" className="etype-link" onClick={() => { setCustom(query); setShowCustom(true); }}>
                    Use it as a custom type
                  </button>
                </p>
              ) : (
                filtered.map((type) => (
                  <button
                    key={type.label}
                    type="button"
                    role="option"
                    aria-selected={value === type.label}
                    className={`etype-item ${value === type.label ? 'is-active' : ''}`}
                    onClick={() => choose(type)}
                  >
                    <span className="etype-emoji" aria-hidden="true">{type.emoji}</span>
                    <span>{type.label}</span>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
    </Portal>
  );
}
